import { PDFDocument, StandardFonts, degrees, rgb } from "pdf-lib";
import { baseFileName } from "./file-name-core.js";

export function hexToRgb(value) {
  const match = /^#([0-9a-f]{6})$/i.exec(value || "");
  if (!match) return { r: 0, g: 0, b: 0 };
  const number = Number.parseInt(match[1], 16);
  return { r: ((number >> 16) & 255) / 255, g: ((number >> 8) & 255) / 255, b: (number & 255) / 255 };
}

export function formatPageNumber(number, total, format) {
  if (format === "page") return `Page ${number}`;
  if (format === "total") return `Page ${number} of ${total}`;
  return String(number);
}

export function calculatePlacement(width, height, textWidth, textHeight, position, margin = 24) {
  const horizontal = position.endsWith("left") ? margin : position.endsWith("right") ? width - margin - textWidth : (width - textWidth) / 2;
  const vertical = position.startsWith("top") ? height - margin - textHeight : position.startsWith("bottom") ? margin : (height - textHeight) / 2;
  return { x: Math.max(0, horizontal), y: Math.max(0, vertical) };
}

export function validateNumberSettings(settings, pageCount) {
  const firstPage = Number(settings.firstPage);
  const start = Number(settings.start);
  if (!Number.isInteger(firstPage) || firstPage < 1 || firstPage > pageCount) return "The first numbered page must be inside this PDF.";
  if (!Number.isInteger(start)) return "The starting number must be a whole number.";
  return null;
}

export function validateWatermarkSettings(settings, pageCount) {
  if (!(settings.text || "").trim()) return "Enter watermark text.";
  if (settings.apply === "range") {
    const from = Number(settings.from); const to = Number(settings.to);
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || from > to || to > pageCount) return `Choose a page range between 1 and ${pageCount}.`;
  }
  return null;
}

export async function addPageNumbers(sourceBytes, settings, onProgress = () => {}) {
  const document = await PDFDocument.load(sourceBytes.slice(), { updateMetadata: false });
  const font = await document.embedFont(StandardFonts.Helvetica);
  const color = hexToRgb(settings.color);
  const size = Number(settings.size);
  const firstPage = Number(settings.firstPage);
  const start = Number(settings.start);
  const pages = document.getPages();
  pages.forEach((page, index) => {
    if (index + 1 >= firstPage) {
      const value = start + index + 1 - firstPage;
      const text = formatPageNumber(value, start + pages.length - firstPage, settings.format);
      const textWidth = font.widthOfTextAtSize(text, size);
      const position = calculatePlacement(page.getWidth(), page.getHeight(), textWidth, size, settings.position);
      page.drawText(text, { ...position, size, font, color: rgb(color.r, color.g, color.b) });
    }
    onProgress(index + 1, pages.length);
  });
  document.setCreator("smallpdf.app"); document.setProducer("smallpdf.app Number PDF Pages");
  return document.save({ useObjectStreams: true });
}

export async function addTextWatermark(sourceBytes, settings, onProgress = () => {}) {
  const document = await PDFDocument.load(sourceBytes.slice(), { updateMetadata: false });
  const font = await document.embedFont(StandardFonts.HelveticaBold);
  const color = hexToRgb(settings.color); const size = Number(settings.size); const text = settings.text.trim();
  const pages = document.getPages();
  pages.forEach((page, index) => {
    const number = index + 1;
    const included = settings.apply === "all" || (number >= Number(settings.from) && number <= Number(settings.to));
    if (included) {
      const textWidth = font.widthOfTextAtSize(text, size);
      const position = calculatePlacement(page.getWidth(), page.getHeight(), textWidth, size, settings.position, 32);
      page.drawText(text, { ...position, size, font, color: rgb(color.r, color.g, color.b), opacity: Number(settings.opacity), rotate: degrees(Number(settings.angle)) });
    }
    onProgress(index + 1, pages.length);
  });
  document.setCreator("smallpdf.app"); document.setProducer("smallpdf.app Watermark PDF");
  return document.save({ useObjectStreams: true });
}

export function numberedFileName(value) { return `${baseFileName(value)}-numbered.pdf`; }
export function watermarkedFileName(value) { return `${baseFileName(value)}-watermarked.pdf`; }
